import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Backdrop, Box, CircularProgress, Typography } from "@mui/material";
import { PlayArrow as IconPlay, Replay as IconReplay, ArrowBackIosNew as IconArrowBackIosNew } from "@mui/icons-material";
import SwipeActionCatcher from "../../components/common/SwipeActionCatcher";
import { getMediaUrl, getStockAudio, isAudioAsset, isImageAsset, isVideoAsset } from "../../utils";
import { AudioPlayer, ImagePlayer, VideoPlayer } from "../../components/players";
import { myToast } from "../../libs";
import CustomControls from "./CustomControls";
import DescriptionBox from "./DescriptionBox";
import { useAppContext } from "../../contexts/AppContext";
import { useLayoutContext } from "../../contexts/LayoutContext";
import { IconButton } from "@mui/material";
import { PhotoCamera as IconPhotoCamera, Close as IconClose } from "@mui/icons-material";
import AssetDrawer from "../../components/CustomDrawers/AssetDrawer/AssetDrawer";
import {
  apiAssetAnalyticsDislikeAsset,
  apiAssetAnalyticsLikeAsset,
  apiAssetAnalyticsFullPlayed,
  apiAssetAnalyticsPartialPlayed,
  apiGetAssetAnalytics,
  apiAssetAnalyticsBookmarkedAsset,
  apiAssetAnalyticsUnbookmarkedAsset,
  apiAssetAnalyticsSkippedAsset,
  apiAssetAnalyticsWatchedAsset,
  apiAssetAnalyticsErrorAsset,
} from "../../graphQL/assetAnalytics";
import PlayerWatch from "../../components/players/PlayerWatch";
import CreatorThumbnail from "../../components/thumbnails/CreatorThumbnails";
import { apiGetUser } from "../../graphQL/user";
import { apiGetAsset } from "../../graphQL/asset";
import CreatorDrawer from "../../components/CustomDrawers/CreatorDrawer/CreatorDrawer";
import NoteDrawer from "../../components/CustomDrawers/NoteDrawer/NoteDrawer";
import ScreenshotRegionSelector from "../../components/resize/ScreenshotRegionSelector";
import { useResizeDetector } from "react-resize-detector";
import DialogCapturedNote from "../../components/dialogs/DialogCapturedNote";
import useAssetNote from "../../hooks/useAssetNote";
import CommentDrawer from "../../components/CustomDrawers/CommentDrawer/CommentDrawer";
import { useDispatch, useSelector } from "react-redux";
import { apiUpdateUserFolder } from "../../graphQL/userFolder";
import { actionUpdateUserFolder } from "../../store/UserFolder.slice";
import BookmarkMenu from "../../components/menus/BookmarkMenu";
import {
  MessageSquare,
  Bookmark,
  ThumbsUp,
  ThumbsDown,
  Info,
  Camera,
  AlertCircle,
  Ticket,
  VolumeX,
  Volume2,
  FileText,
} from "lucide-react";
import { isMobile } from "react-device-detect";
import ComponentDrawer from "../../components/CustomDrawers/ComponentDrawer/ComponentDrawer";
import DescriptionDrawer from "../../components/CustomDrawers/DescriptionDrawer/DescriptionDrawer";
import { GUEST } from "../../config";
import { useConfirm } from "material-ui-confirm";
import { signOutAndRedirect } from "../../utils/authSession";
import ReservationDrawer from "../../components/CustomDrawers/ReservationDrawer/ReservationDrawer";
import CategoryBar from "./CategoryBar";

const isDebug = false;

const BigPlayButton = ({ onClick, isReplay }) => {
  return (
    <Box
      sx={{
        position: "absolute",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        zIndex: 20,
      }}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
    >
      <Box
        className="icon-replay"
        display="flex"
        alignItems="center"
        justifyContent="center"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        sx={{
          width: "6rem",
          height: "6rem",
          borderRadius: "50%",
          backgroundColor: "#454545aa",
          color: "#f2f2f2",
          cursor: "pointer",
        }}
      >
        {isReplay ? (
          <IconReplay sx={{ fontSize: "4rem" }}></IconReplay>
        ) : (
          <IconPlay sx={{ fontSize: "4rem" }}></IconPlay>
        )}
      </Box>
      {isReplay && <Typography variant="h6">Replay</Typography>}
    </Box>
  );
};

function ForYouAssetPlayer({ assets, filter = {}, setFilter = () => {}, onLoadMore = () => {} }) {
  const playerRef = useRef();
  const InfoButtonRef = useRef();
  const CreatorButtonRef = useRef();
  const WatchButtonRef = useRef();
  const CaptureRef = useRef();
  const ImageRef = useRef();
  const dispatch = useDispatch();
  const confirm = useConfirm();

  const { width, height, ref: assetPlayerBoxRef } = useResizeDetector();

  const [{ statusOfAssets, volume }, { actionLikeAnAsset, actionDislikeAnAsset }] = useAppContext();
  const [, { setShowTopAppBar, setVideoWindowSize }] = useLayoutContext();
  const userFolders = useSelector((store) => store.UserFolder.userFolders);
  const stockAudios = useSelector((store) => store.StockAudio.stockAudios);
  const categories = useSelector((store) => store.Category.categories);
  const [noteState, setNoteState] = useState({
    showed: false,
    pinned: false,
    note: null,
  });

  const [currentIdx, setCurrentIdx] = useState(0);
  const [currentAsset, setCurrentAsset] = useState(null);
  const [soundTrack, setSoundTrack] = useState(null);
  const skippedAssetRef = useRef(null);
  const triedPlaybackUrlsRef = useRef({});
  const videoStartTimeoutRef = useRef(null);
  const videoStartedRef = useRef(false);
  const watchedAnalyticsSentRef = useRef(null);
  const lastCategoryIdRef = useRef(null);

  // Round-robin scheduler state
  const watchedSetRef = useRef(new Set());        // asset IDs that have been fully or partially played
  const categoryPlayCountRef = useRef(0);         // how many videos played in current category this round
  const categorySkipCountRef = useRef(0);         // consecutive skips in current category
  const currentCategoryIndexRef = useRef(0);      // index into nonEmptyCategories
  const categoryAssetIndexRef = useRef(new Map()); // categoryId -> next asset index within that category's bucket
  const [{ domainInfo }] = useAppContext();
  const PrivateInfo = useSelector((store) => store.auth.PrivateInfo);
  
  // Local state for assets that can be modified (for parent asset switching)
  const [localAssets, setLocalAssets] = useState(assets);

  const domainName = filter?.domain || domainInfo?.name;
  const enableCategoryNav = Boolean(filter?.domain && filter?.pagination);
  const { playbackAssets, nonEmptyCategories, categoryStartIndex, assetCategoryId, categoryBuckets, categoryImageMap } = useMemo(() => {
    if (!enableCategoryNav) {
      return {
        playbackAssets: localAssets || [],
        nonEmptyCategories: [],
        categoryStartIndex: new Map(),
        assetCategoryId: new Map(),
        categoryBuckets: new Map(),
      };
    }

    const domainCategories = (categories || []).filter((cat) => !domainName || cat.domain === domainName);
    const buckets = new Map();
    const imageMap = new Map(); // categoryId -> first image asset (shown on category transition)
    domainCategories.forEach((cat) => { buckets.set(cat.id, []); });

    const assetCategoryIdMap = new Map();
    (localAssets || []).forEach((asset) => {
      const categoryId = asset?.categories?.find((id) => buckets.has(id));
      if (categoryId) {
        if (isImageAsset(asset)) {
          // Store first image per category for transition display
          if (!imageMap.has(categoryId)) imageMap.set(categoryId, asset);
          assetCategoryIdMap.set(asset.id, categoryId);
        } else {
          buckets.get(categoryId).push(asset);
          assetCategoryIdMap.set(asset.id, categoryId);
        }
      }
    });

    const nonEmpty = domainCategories.filter((cat) => (buckets.get(cat.id) || []).length > 0);
    const categoryStart = new Map();
    let orderedAssets = [];
    nonEmpty.forEach((cat) => {
      categoryStart.set(cat.id, orderedAssets.length);
      orderedAssets = orderedAssets.concat(buckets.get(cat.id));
    });

    if (nonEmpty.length === 0) {
      return {
        playbackAssets: localAssets || [],
        nonEmptyCategories: [],
        categoryStartIndex: new Map(),
        assetCategoryId: assetCategoryIdMap,
        categoryBuckets: buckets,
        categoryImageMap: imageMap,
      };
    }

    return {
      playbackAssets: orderedAssets,
      nonEmptyCategories: nonEmpty,
      categoryStartIndex: categoryStart,
      assetCategoryId: assetCategoryIdMap,
      categoryBuckets: buckets,
      categoryImageMap: imageMap,
    };
  }, [categories, localAssets, domainName, enableCategoryNav]);

  const currentCategoryId = assetCategoryId.get(currentAsset?.id);
  const shouldShowCategoryBar = nonEmptyCategories?.length > 0;

  // ── Round-robin helpers ──
  // All scheduler logic uses refs only (no state) to avoid re-render loops.
  const VIDEOS_PER_CATEGORY = 4;
  const categoryBucketsRef = useRef(categoryBuckets);
  const nonEmptyCategoriesRef = useRef(nonEmptyCategories);
  const categoryImageMapRef = useRef(categoryImageMap);
  categoryBucketsRef.current = categoryBuckets;
  nonEmptyCategoriesRef.current = nonEmptyCategories;
  categoryImageMapRef.current = categoryImageMap;
  const showingCategoryImageRef = useRef(false); // true when currently showing a category intro image

  const getNextUnwatchedInCategory = useCallback((categoryId) => {
    const bucket = categoryBucketsRef.current.get(categoryId);
    if (!bucket?.length) return null;
    const startIdx = categoryAssetIndexRef.current.get(categoryId) || 0;
    for (let i = 0; i < bucket.length; i++) {
      const idx = (startIdx + i) % bucket.length;
      if (!watchedSetRef.current.has(bucket[idx].id)) {
        return { asset: bucket[idx], bucketIndex: idx };
      }
    }
    return null;
  }, []);

  const countUnwatchedInCategory = useCallback((categoryId) => {
    const bucket = categoryBucketsRef.current.get(categoryId);
    if (!bucket?.length) return 0;
    return bucket.filter((a) => !watchedSetRef.current.has(a.id)).length;
  }, []);

  const advanceToNextCategory = useCallback(() => {
    const cats = nonEmptyCategoriesRef.current;
    if (!cats.length) return null;
    const startIdx = currentCategoryIndexRef.current;
    for (let i = 1; i <= cats.length; i++) {
      const idx = (startIdx + i) % cats.length;
      if (countUnwatchedInCategory(cats[idx].id) > 0) {
        currentCategoryIndexRef.current = idx;
        categoryPlayCountRef.current = 0;
        categorySkipCountRef.current = 0;
        return cats[idx].id;
      }
    }
    // All exhausted — reset for a new round
    watchedSetRef.current.clear();
    categoryAssetIndexRef.current = new Map();
    currentCategoryIndexRef.current = (startIdx + 1) % cats.length;
    categoryPlayCountRef.current = 0;
    categorySkipCountRef.current = 0;
    return cats[currentCategoryIndexRef.current]?.id || null;
  }, [countUnwatchedInCategory]);

  // Pick the next asset. Uses only refs — safe to call from any handler without causing re-renders.
  const pickNextAsset = useCallback((wasSkipped, currentAssetId) => {
    const cats = nonEmptyCategoriesRef.current;
    if (!cats.length) return null;

    // If we just showed a category intro image, proceed to first video (don't count as play/skip)
    if (showingCategoryImageRef.current) {
      showingCategoryImageRef.current = false;
      const targetCatId = cats[currentCategoryIndexRef.current]?.id;
      if (targetCatId) {
        const next = getNextUnwatchedInCategory(targetCatId);
        if (next) {
          categoryAssetIndexRef.current.set(targetCatId, next.bucketIndex + 1);
          return next.asset;
        }
      }
    }

    if (currentAssetId) {
      watchedSetRef.current.add(currentAssetId);
    }

    let targetCatId = cats[currentCategoryIndexRef.current]?.id;
    const prevCatId = targetCatId;

    if (wasSkipped) {
      categorySkipCountRef.current += 1;
      const unwatched = countUnwatchedInCategory(targetCatId);

      if (unwatched <= 2) {
        targetCatId = advanceToNextCategory();
      } else if (categorySkipCountRef.current === 1) {
        categoryPlayCountRef.current = VIDEOS_PER_CATEGORY - 1;
      } else {
        targetCatId = advanceToNextCategory();
      }
    } else {
      categoryPlayCountRef.current += 1;
      categorySkipCountRef.current = 0;

      if (categoryPlayCountRef.current >= VIDEOS_PER_CATEGORY) {
        targetCatId = advanceToNextCategory();
      }
    }

    if (!targetCatId) return null;

    // Show category intro image on category transition
    if (targetCatId !== prevCatId) {
      const catImage = categoryImageMapRef.current.get(targetCatId);
      if (catImage) {
        showingCategoryImageRef.current = true;
        return catImage;
      }
    }

    const next = getNextUnwatchedInCategory(targetCatId);
    if (next) {
      categoryAssetIndexRef.current.set(targetCatId, next.bucketIndex + 1);
      return next.asset;
    }

    targetCatId = advanceToNextCategory();
    if (!targetCatId) return null;

    // Show category intro image for fallback category too
    const catImage = categoryImageMapRef.current.get(targetCatId);
    if (catImage) {
      showingCategoryImageRef.current = true;
      return catImage;
    }

    const fallback = getNextUnwatchedInCategory(targetCatId);
    if (fallback) {
      categoryAssetIndexRef.current.set(targetCatId, fallback.bucketIndex + 1);
      return fallback.asset;
    }
    return null;
  }, [countUnwatchedInCategory, advanceToNextCategory, getNextUnwatchedInCategory]);

  const [isReady, setReady] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isMuted, setMuted] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const [isReplay, setReplay] = useState(false);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [duration, setDuration] = useState(15);
  const [mediaUrl, setMediaUrl] = useState("");
  const [playbackError, setPlaybackError] = useState(null);
  const [openScreenshotSelector, setOpenScreenshotSelector] = useState(false);
  const [selectedArea, setSelectedArea] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const handleScreenshotSelect = useCallback(({ width: _width, height: _height, x: clientX, y: clientY }) => {
    setSelectedArea((prev) => {
      if (
        prev.width === _width &&
        prev.height === _height &&
        prev.x === clientX &&
        prev.y === clientY
      ) {
        return prev;
      }
      return {
        width: _width,
        height: _height,
        x: clientX,
        y: clientY,
      };
    });
  }, []);

  const [snapshotFile, setSnapshotFile] = useState(null);

  const playerStatus = {
    loading: isLoading,
    muted: isMuted,
    playing: isPlaying,
    replay: setReplay,
    playedSeconds,
    duration,
  };
  const [assetOwner, setAssetOwner] = useState(null);
  const [openAssetDrawer, setOpenAssetDrawer] = useState(false);
  const [openCreatorDrawer, setOpenCreatorDrawer] = useState(false);
  const [openCommentDrawer, setOpenCommentDrawer] = useState(false);
  const [openDirectCommentDrawer, setOpendirectCommentDrawer] = useState(false);
  const bookmarkButtonRef = useRef(null);
  const [openBookmarkMenu, setOpenBookmarkMenu] = useState(false);
  const [openDescriptionDrawer, setOpenDescriptionDrawer] = useState(false);
  const [openNoteEditor, setOpenNoteEditor] = useState(false);
  const [openReservationDrawer, setOpenReservationDrawer] = useState(false);

  const [analytics, setAnalytics] = useState(null);
  const [originalHighlightAsset, setOriginalHighlightAsset] = useState(null);
  const [isViewingParent, setIsViewingParent] = useState(false);
  const [isParentPublished, setIsParentPublished] = useState(false);

  const resolveAssetUrl = useCallback((value) => {
    if (!value) return null;
    if (value.includes("https:/") || value.includes("http:/") || value.includes("file:/")) {
      return value;
    }
    const base = process.env.REACT_APP_HLS_BASE_URL || "";
    if (!base) return value;
    if (value.startsWith("/")) return `${base}${value}`;
    return `${base}/${value}`;
  }, []);

  const getVideoPlaybackCandidates = useCallback(
    (asset) => {
      if (!asset) return [];
      const candidates = [];
      const push = (url) => {
        const normalized = resolveAssetUrl(url);
        if (normalized && !candidates.includes(normalized)) {
          candidates.push(normalized);
        }
      };

      push(asset?.file?.outputHls);
      push(asset?.file?.outputMP4);
      push(asset?.media?.extUrl);
      push(getMediaUrl(asset));

      return candidates;
    },
    [resolveAssetUrl]
  );
  const parentPublishedCacheRef = useRef(new Map());
  
  // Sync localAssets with assets prop when it changes
  useEffect(() => {
    // Only update if we're not viewing a parent asset to avoid overwriting
    if (!isViewingParent) {
      setLocalAssets(assets);
    }
  }, [assets]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentAsset) {
      apiGetAssetAnalytics(currentAsset?.id).then((res) => {
        setAnalytics(res);
      });
    } else {
      setAnalytics(null);
    }
  }, [currentAsset]);

  useEffect(() => {
    let cancelled = false;

    // Only relevant on highlight assets (not while viewing parent)
    if (isViewingParent) {
      setIsParentPublished(false);
      return () => {
        cancelled = true;
      };
    }

    const parentId = currentAsset?.parentId;
    if (!parentId) {
      setIsParentPublished(false);
      return () => {
        cancelled = true;
      };
    }

    const cached = parentPublishedCacheRef.current.get(parentId);
    if (typeof cached === "boolean") {
      setIsParentPublished(cached);
      return () => {
        cancelled = true;
      };
    }

    // Unknown -> fetch parent once, cache publish status
    apiGetAsset(parentId)
      .then((parentAsset) => {
        const published = parentAsset?.status === "Published";
        parentPublishedCacheRef.current.set(parentId, published);
        if (!cancelled) setIsParentPublished(published);
      })
      .catch(() => {
        parentPublishedCacheRef.current.set(parentId, false);
        if (!cancelled) setIsParentPublished(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentAsset?.parentId, isViewingParent]);

  const assetType = isImageAsset(currentAsset) ? "Image" : isAudioAsset(currentAsset) ? "Audio" : "Video";
  const [{ assetNote }] = useAssetNote(currentAsset?.id);
  const isVideoPlaying =
    isPlaying &&
    !openAssetDrawer &&
    !openScreenshotSelector &&
    !openCommentDrawer &&
    !openBookmarkMenu &&
    !openDirectCommentDrawer &&
    !openNoteEditor &&
    !openReservationDrawer &&
    !openCreatorDrawer &&
    !openDescriptionDrawer;

  const onPrev = () => {
    if (!playbackAssets?.length) return;
    setReady(false);
    setReplay(false);
    setCurrentIdx((prevIdx) => (prevIdx + playbackAssets.length - 1) % playbackAssets.length);
    setPlayedSeconds(0);
  };

  const navigateToAsset = useCallback((asset) => {
    if (!asset) return;
    const idx = playbackAssets.findIndex((a) => a.id === asset.id);
    if (idx !== -1) {
      setReady(false);
      setReplay(false);
      setPlayedSeconds(0);
      setCurrentIdx(idx);
    }
  }, [playbackAssets]);

  const onNext = () => {
    if (!playbackAssets?.length) return;
    if (enableCategoryNav && nonEmptyCategoriesRef.current.length) {
      const next = pickNextAsset(false, currentAsset?.id);
      if (next) { navigateToAsset(next); return; }
    }
    setReady(false);
    setReplay(false);
    setCurrentIdx((prevIdx) => (prevIdx + 1) % playbackAssets.length);
    setPlayedSeconds(0);
  };

  const onSkipNext = () => {
    if (!playbackAssets?.length) return;
    if (enableCategoryNav && nonEmptyCategoriesRef.current.length) {
      const next = pickNextAsset(true, currentAsset?.id);
      if (next) { navigateToAsset(next); return; }
    }
    setReady(false);
    setReplay(false);
    setCurrentIdx((prevIdx) => (prevIdx + 1) % playbackAssets.length);
    setPlayedSeconds(0);
  };

  const onActionPrev = (fromVideoEnd = false) => {
    // If viewing parent asset, go back to original highlight segment
    if (isViewingParent && originalHighlightAsset) {
      handleBackToOriginal();
      return;
    }

    if (!playbackAssets?.length || playbackAssets.length <= 1) {
      setLoading(false);
      setPlaying(false);
      return;
    }

    setLoading(true);
    // Only track partial play if user actually started watching
    if (!fromVideoEnd && playedSeconds > 0) {
      apiAssetAnalyticsPartialPlayed(currentAsset?.id).then(() => {});
    }
    onPrev();
  };

  const onActionNext = (fromVideoEnd = false) => {
    if (!playbackAssets?.length || playbackAssets.length <= 1) {
      setLoading(false);
      setPlaying(false);
      return;
    }

    setLoading(true);
    if (!fromVideoEnd) {
      // Track as skipped + partial (only if video actually started)
      apiAssetAnalyticsSkippedAsset(currentAsset?.id).catch(() => {});
      if (playedSeconds > 0) {
        apiAssetAnalyticsPartialPlayed(currentAsset?.id).catch(() => {});
      }
      onSkipNext(); // user manually skipped
    } else {
      onNext(); // video ended naturally
    }
  };

  const handleWatchFullVideo = async () => {
    if (!currentAsset?.parentId) return;
    
    // Save the current highlight segment as the original
    setOriginalHighlightAsset(currentAsset);
    setIsViewingParent(true);
    setLoading(true);
    
    try {
      // Fetch the parent asset
      const parentAsset = await apiGetAsset(currentAsset.parentId);
      // Update local assets array to include the parent asset at the current index
      const newAssets = [...localAssets];
      const localIndex = newAssets.findIndex((asset) => asset?.id === currentAsset?.id);
      if (localIndex !== -1) {
        newAssets[localIndex] = parentAsset;
        setLocalAssets(newAssets);
      }
      
      // Set the current asset to the parent
      setCurrentAsset(parentAsset);
      setPlayedSeconds(0);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching parent asset:", error);
      myToast.error("Failed to load full video");
      
      // Send error analytics for failed parent asset fetch
      if (currentAsset?.parentId) {
        apiAssetAnalyticsErrorAsset(currentAsset.parentId, `Failed to load full video: ${error.message || "Unknown error"}`).catch((err) => {
          console.error("Failed to send error analytics:", err);
        });
      }
      
      setIsViewingParent(false);
      setOriginalHighlightAsset(null);
      setLoading(false);
    }
  };

  const handleBackToOriginal = () => {
    if (isViewingParent && originalHighlightAsset) {
      // Return to the original highlight segment
      const newAssets = [...localAssets];
      const localIndex = newAssets.findIndex(
        (asset) => asset?.id === currentAsset?.id || asset?.id === originalHighlightAsset?.id,
      );
      if (localIndex !== -1) {
        newAssets[localIndex] = originalHighlightAsset;
        setLocalAssets(newAssets);
      }
      setCurrentAsset(originalHighlightAsset);
      setIsViewingParent(false);
      setOriginalHighlightAsset(null);
      setPlayedSeconds(0);
      setReady(false);
      setReplay(false);
    }
  };

  const addAssetToDefaultBookmarkFolder = async (asset) => {
    const defaultFolder = userFolders?.length > 0 ? userFolders[0] : null;
    if (defaultFolder) {
      addAssetBookmark(defaultFolder, asset);
    }
  };

  const addAssetBookmark = async (userFolder, asset) => {
    if (userFolder) {
      let res = await apiUpdateUserFolder({
        id: userFolder?.id,
        assetIds: [
          ...(userFolder?.assetIds
            ?.filter((t) => t.id !== asset?.id)
            .map((t) => ({
              id: t?.id,
              note: t?.note,
            })) || []),
          { id: asset?.id, note: "" },
        ],
      });
      dispatch(actionUpdateUserFolder(res));
      apiAssetAnalyticsBookmarkedAsset(asset?.id);
    }
  };

  const removeAssetBookmark = async (asset) => {
    const userFolder = userFolders?.find((userFolder) => {
      return userFolder?.assetIds?.map((t) => t.id).includes(asset?.id);
    });
    if (userFolder) {
      let res = await apiUpdateUserFolder({
        id: userFolder?.id,
        assetIds: userFolder?.assetIds
          ?.filter((t) => t.id !== asset?.id)
          .map((t) => ({
            id: t?.id,
            note: t?.note,
          })),
      });
      dispatch(actionUpdateUserFolder(res));
      apiAssetAnalyticsUnbookmarkedAsset(asset?.id);
    }
  };

  const isBookmarked = (asset) => {
    if (!userFolders || userFolders?.length === 0) return false;
    return userFolders?.some((userFolder) => {
      return userFolder?.assetIds?.map((t) => t.id).includes(asset?.id);
    });
  };

  const onHandleComponentAction = (action) => {
    if (PrivateInfo.name === GUEST.email) {
      confirm({
        title: "You are in guest mode. Please register a free account to unlock this feature.",
        confirmationText: "SIGN IN",
        cancellationText: "BACK",
        titleProps: {
          textAlign: "center",
        },
        cancellationButtonProps: {
          style: {
            background: "white",
            color: "black",
          },
        },
        confirmationButtonProps: {
          style: {
            background: "white",
            color: "black",
          },
        },
        dialogActionsProps: {
          style: {
            justifyContent: "space-between",
          },
        },
      })
        .then(() => {
          signOutAndRedirect();
        })
        .catch(() => {});
      return;
    }
    if (action === "PrivateComment") {
      setOpendirectCommentDrawer(true);
      setPlaying(false);
    } else if (action === "Comment") {
      setOpenCommentDrawer(true);
      setPlaying(false);
    } else if (action === "Bookmark") {
      if (isBookmarked(currentAsset)) {
        removeAssetBookmark(currentAsset).then(() => {
          setOpenBookmarkMenu(false);
        });
      } else {
        setOpenBookmarkMenu(true);
        setPlaying(false);
      }
    } else if (action === "Thumb up") {
      if (!statusOfAssets[currentAsset?.id]?.liked) {
        apiAssetAnalyticsLikeAsset(currentAsset?.id).then((data) => {
          setAnalytics(data);
          actionLikeAnAsset(currentAsset?.id);
        });
      } else {
        actionLikeAnAsset(currentAsset?.id, false);
      }
    } else if (action === "Thumb down") {
      if (!statusOfAssets[currentAsset?.id]?.disliked) {
        apiAssetAnalyticsDislikeAsset(currentAsset?.id).then((data) => {
          setAnalytics(data);
          actionDislikeAnAsset(currentAsset?.id);
        });
      } else {
        actionDislikeAnAsset(currentAsset?.id, false);
      }
    } else if (action === "Information") {
      setPlaying(false);
      setOpenAssetDrawer(true);
    } else if (action === "Screenshot") {
      setPlaying(false);
      setOpenScreenshotSelector(true);
    } else if (action === "PlayerWatch") {
    } else if (action === "CreatorThumbnail") {
      setPlaying(false);
      setOpenCreatorDrawer(true);
    } else if (action === "Info+") {
      setPlaying(false);
      setOpenDescriptionDrawer(true);
    } else if (action === "Reservation") {
      setPlaying(false);
      setOpenReservationDrawer(true);
    }
  };

  useEffect(() => {
    if (playbackAssets?.length) {
      if (currentIdx < 0) return;
      if (currentIdx >= playbackAssets.length) {
        setCurrentIdx(0);
        return;
      }

      const asset = playbackAssets[currentIdx];
      // Only update when the asset at current index actually changed
      if (asset?.id !== currentAsset?.id) {
        setCurrentAsset(asset);

        // Sync scheduler category index with the asset being played
        if (enableCategoryNav && nonEmptyCategories.length) {
          const catId = assetCategoryId.get(asset?.id);
          const catIdx = nonEmptyCategories.findIndex((c) => c.id === catId);
          if (catIdx !== -1 && catIdx !== currentCategoryIndexRef.current) {
            currentCategoryIndexRef.current = catIdx;
            categoryPlayCountRef.current = 0;
            categorySkipCountRef.current = 0;
          }
        }
      }
    } else {
      if (currentAsset != null) {
        setCurrentAsset(null);
      }
    }
  }, [playbackAssets, currentIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!playbackAssets?.length || !currentAsset?.id) return;
    const updatedIdx = playbackAssets.findIndex((asset) => asset?.id === currentAsset?.id);
    if (updatedIdx !== -1 && updatedIdx !== currentIdx) {
      setCurrentIdx(updatedIdx);
    }
  }, [playbackAssets, currentAsset?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger load more when 2 videos are left
  useEffect(() => {
    if (playbackAssets?.length && currentIdx >= 0) {
      const remainingVideos = playbackAssets.length - currentIdx - 1;
      if (remainingVideos === 2) {
        // Trigger load more callback when 2 videos are left
        onLoadMore({ categoryId: currentCategoryId, reason: "nearEnd" });
      }
    }
  }, [currentIdx, playbackAssets?.length, onLoadMore, currentCategoryId]);

  useEffect(() => {
    if (!enableCategoryNav || !currentCategoryId) return;
    if (lastCategoryIdRef.current === currentCategoryId) return;
    lastCategoryIdRef.current = currentCategoryId;
    onLoadMore({ categoryId: currentCategoryId, reason: "enterCategory" });
  }, [currentCategoryId, enableCategoryNav, onLoadMore]);

  useEffect(() => {
    setNoteState({
      showed: false,
      pinned: false,
      note: null,
    });
    setPlaybackError(null);

    const playbackCandidates = isVideoAsset(currentAsset) ? getVideoPlaybackCandidates(currentAsset) : [];
    const mediaUrlValue = isVideoAsset(currentAsset) ? playbackCandidates[0] : getMediaUrl(currentAsset);
    setMediaUrl(mediaUrlValue);

    if (isVideoAsset(currentAsset) && currentAsset?.id) {
      triedPlaybackUrlsRef.current[currentAsset.id] = mediaUrlValue ? [mediaUrlValue] : [];
    }
    
    // If video is not reachable (no valid media URL), skip to next
    if (isVideoAsset(currentAsset) && !mediaUrlValue && currentAsset?.id !== skippedAssetRef.current) {
      
      // Send error analytics for unreachable video
      if (currentAsset?.id) {
        apiAssetAnalyticsErrorAsset(currentAsset.id, "Video not reachable - no valid media URL").catch((err) => {
          console.error("Failed to send error analytics:", err);
        });
      }
      
      skippedAssetRef.current = currentAsset?.id;
      if (playbackAssets?.length > 1) {
        setTimeout(() => {
          onActionNext(true); // Auto-skip on unreachable video, don't track as partial
        }, 100);
      }
      return;
    }
    
    // Reset skipped asset ref when we successfully load a new asset
    if (currentAsset?.id && currentAsset?.id !== skippedAssetRef.current) {
      skippedAssetRef.current = null;
    }
    
    // Clear any existing video start timeout
    if (videoStartTimeoutRef.current) {
      clearTimeout(videoStartTimeoutRef.current);
      videoStartTimeoutRef.current = null;
    }
    
    // Reset video started flag and watched analytics tracking for new asset
    videoStartedRef.current = false;
    watchedAnalyticsSentRef.current = null;
    
    // Set up 10-second timeout check for video assets
    if (isVideoAsset(currentAsset) && mediaUrlValue && currentAsset?.id) {
      const assetId = currentAsset.id;
      videoStartTimeoutRef.current = setTimeout(() => {
        // Check if video hasn't started playing within 10 seconds
        if (!videoStartedRef.current && currentAsset?.id === assetId) {
          
          // Send error analytics for video timeout
          apiAssetAnalyticsErrorAsset(assetId, "Video playback timeout - did not start within 10 seconds").catch((err) => {
            console.error("Failed to send error analytics:", err);
          });
          
          skippedAssetRef.current = assetId;
          if (playbackAssets?.length > 1) {
            onActionNext(true); // Auto-skip on timeout, don't track as partial
          } else {
            setLoading(false);
            setPlaying(false);
          }
        }
      }, 10000); // 10 seconds timeout
    }
    
    if (currentAsset?.stockAudioId) {
      const stockAudio = stockAudios?.find((audio) => audio.id === currentAsset.stockAudioId);
      setSoundTrack(stockAudio);
    }
    if (isImageAsset(currentAsset)) {
      setPlayedSeconds(0);
      setDuration(15);
      setLoading(false);
      setPlaying(true);
    } else {
      setPlaying(true);
    }
    
    // Cleanup timeout on unmount or asset change
    return () => {
      if (videoStartTimeoutRef.current) {
        clearTimeout(videoStartTimeoutRef.current);
        videoStartTimeoutRef.current = null;
      }
    };
  }, [currentAsset, assets, stockAudios, playbackAssets?.length, getVideoPlaybackCandidates]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isImageAsset(currentAsset) && isVideoPlaying) {
      let time = playedSeconds;
      const timer = setInterval(() => {
        time += 0.1;
        setPlayedSeconds(time);
        if (time >= 15) {
          // Image fully displayed for its duration, track as full play
          apiAssetAnalyticsFullPlayed(currentAsset?.id);
          onActionNext(true); // Image auto-advance after duration, don't track as partial
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [currentAsset, isVideoPlaying]); //eslint-disable-line

  useEffect(() => {
    if (currentAsset?.owner) {
      apiGetUser(currentAsset?.owner)
        .then((res) => {
          setAssetOwner(res);
        })
        .catch((err) => {
          console.log(err);
          setAssetOwner(null);
        });
    }
  }, [currentAsset?.owner]);

  useEffect(() => {
    if (assetNote) {
      const { notes } = assetNote;
      if (notes && notes?.length > 0) {
        const note = notes?.find((t) => t.start <= playedSeconds && t.start + 0.5 >= playedSeconds);
        if (note) {
          setNoteState({
            ...noteState,
            showed: true,
            note,
          });
        } else {
          if (noteState?.note?.start <= playedSeconds && noteState?.note?.start + 10 >= playedSeconds) return;
          setNoteState({
            showed: false,
            pinned: false,
            note: null,
          });
        }
      }
    }
  }, [assetNote, playedSeconds]);

  useEffect(() => {
    setVideoWindowSize({ width, height });
  }, [width, height]); //eslint-disable-line

  const jumpToCategory = useCallback((categoryId) => {
    const cats = nonEmptyCategoriesRef.current;
    const catIdx = cats.findIndex((c) => c.id === categoryId);
    if (catIdx === -1) return;

    currentCategoryIndexRef.current = catIdx;
    categoryPlayCountRef.current = 0;
    categorySkipCountRef.current = 0;

    const next = getNextUnwatchedInCategory(categoryId);
    if (next) {
      categoryAssetIndexRef.current.set(categoryId, next.bucketIndex + 1);
      setLoading(true);
      if (playedSeconds > 0) {
        apiAssetAnalyticsPartialPlayed(currentAsset?.id).catch(() => {});
      }
      navigateToAsset(next.asset);
    } else {
      const targetIndex = categoryStartIndex.get(categoryId);
      if (targetIndex === undefined) return;
      setLoading(true);
      if (playedSeconds > 0) {
        apiAssetAnalyticsPartialPlayed(currentAsset?.id).catch(() => {});
      }
      setReady(false);
      setReplay(false);
      setPlayedSeconds(0);
      setCurrentIdx(targetIndex);
    }
  }, [getNextUnwatchedInCategory, navigateToAsset, categoryStartIndex, currentAsset, playedSeconds]);

  const handleNext = useCallback(() => {
    if (!nonEmptyCategories?.length) return;
    const curCatId = assetCategoryId.get(currentAsset?.id);
    const curIdx = nonEmptyCategories.findIndex((cat) => cat.id === curCatId);
    const nextIdx = curIdx === -1 ? 0 : (curIdx + 1) % nonEmptyCategories.length;
    jumpToCategory(nonEmptyCategories[nextIdx].id);
  }, [nonEmptyCategories, assetCategoryId, currentAsset, jumpToCategory]);

  const handlePrev = useCallback(() => {
    if (!nonEmptyCategories?.length) return;
    const curCatId = assetCategoryId.get(currentAsset?.id);
    const curIdx = nonEmptyCategories.findIndex((cat) => cat.id === curCatId);
    const prevIdx = curIdx === -1
      ? nonEmptyCategories.length - 1
      : (curIdx + nonEmptyCategories.length - 1) % nonEmptyCategories.length;
    jumpToCategory(nonEmptyCategories[prevIdx].id);
  }, [nonEmptyCategories, assetCategoryId, currentAsset, jumpToCategory]);

  return (
    <>
      <SwipeActionCatcher
        onSwipeDown={() => {
          setShowTopAppBar(true);
          onActionPrev();
        }}
        onSwipeUp={() => {
          onActionNext();
        }}
        onSwipeLeft={() => {
          handlePrev();
        }}
        onSwipeRight={() => {
          handleNext();
        }}
      >
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: 999,
            position: "absolute",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
          }}
          open={openScreenshotSelector}
        >
          <div style={{ width: "100%", height: "100%", position: "relative" }} ref={assetPlayerBoxRef}>
            <Box sx={{ position: "absolute", top: 10, right: 10 }}>
              <IconButton
                onClick={() => {
                  (() => {
                    const canvas = document.createElement("canvas");
                    canvas.width = selectedArea.width;
                    canvas.height = selectedArea.height;
                    const ctx = canvas.getContext("2d");

                    ctx.drawImage(
                      isImageAsset(currentAsset) ? ImageRef.current : playerRef.current.getInternalPlayer(),
                      selectedArea.x,
                      selectedArea.y,
                      selectedArea.width,
                      selectedArea.height,
                      0,
                      0,
                      selectedArea.width,
                      selectedArea.height
                    );
                    return Promise.resolve(canvas);
                  })().then((canvas) => {
                    canvas.toBlob((file) => {
                      setSnapshotFile(new File([file], "1.png"));
                    }, "image/png");
                  });
                }}
              >
                <IconPhotoCamera fontSize="large"></IconPhotoCamera>
              </IconButton>
              <IconButton
                onClick={() => {
                  setOpenScreenshotSelector(false);
                  setPlaying(true);
                }}
              >
                <IconClose fontSize="large"></IconClose>
              </IconButton>
            </Box>
            <ScreenshotRegionSelector
              openScreenshotSelector={openScreenshotSelector}
              onSelect={handleScreenshotSelect}
            ></ScreenshotRegionSelector>
          </div>
        </Backdrop>
        {!isVideoPlaying && !isLoading && (
          <BigPlayButton
            onClick={() => {
              if (!isReplay) {
                setPlaying(true);
              } else {
                onActionNext(false); // Manual replay, so track as partial if they skip
              }
            }}
            isReplay={isReplay}
          ></BigPlayButton>
        )}
        {playbackAssets?.length ? <Backdrop sx={{ color: "#fff", zIndex: 20, position: "absolute" }} open={isLoading || !currentAsset}>
          <CircularProgress sx={{ color: "#fff" }} />
        </Backdrop> : ""}
        {playbackAssets?.length < 1 ? <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: "100%",
              height: "100%",
            }}
          >
            <Typography variant="h5" color="common.white">
              No asset available at this time
            </Typography>
          </Box> : <></>}
        <Box sx={{ height: "100%", width: "100%", position: "relative" }}>
          {/* Back Arrow - Always visible, takes you back to highlight reels when viewing parent */}
          {isViewingParent && (
            <IconButton
              onClick={handleBackToOriginal}
              sx={{
                position: "absolute",
                top: 16,
                left: 16,
                zIndex: 1000,
                color: "white",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                backdropFilter: "blur(10px)",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                },
              }}
            >
              <IconArrowBackIosNew />
            </IconButton>
          )}
          {/* Watch Full Video pill — top-left corner, inside player stacking context */}
          {!isViewingParent && currentAsset?.parentId && (
            <Box
              onClick={(e) => { e.stopPropagation(); handleWatchFullVideo(); }}
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 40,
                px: 2.5,
                py: 0.8,
                borderRadius: 20,
                background: 'rgba(124, 58, 237, 0.85)',
                backdropFilter: 'blur(8px)',
                color: '#fff',
                fontSize: { xs: 12, md: 14 },
                fontWeight: 600,
                cursor: 'pointer',
                pointerEvents: 'auto',
                '&:hover': { background: 'rgba(124, 58, 237, 1)' },
              }}
            >
              Watch Full Video
            </Box>
          )}
          {/* X button to go back when viewing full video */}
          {isViewingParent && (
            <Box
              onClick={(e) => { e.stopPropagation(); handleBackToOriginal(); }}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 40,
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(8px)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                fontWeight: 600,
                cursor: 'pointer',
                pointerEvents: 'auto',
              }}
            >
              &#10005;
            </Box>
          )}
          <Box sx={{ height: "100%", width: "100%", position: "relative" }} ref={CaptureRef}>
            {isImageAsset(currentAsset) && (
              <>
                <ImagePlayer
                  imageRef={ImageRef}
                  url={getMediaUrl(currentAsset)}
                  onClick={() => {
                    setPlaying(false);
                  }}
                ></ImagePlayer>
                {soundTrack ? (
                  <audio id="audio" loop autoplay>
                    <source src={getStockAudio(soundTrack)} type={soundTrack?.file?.contentType} />
                  </audio>
                ) : (
                  <></>
                )}
              </>
            )}
            {isAudioAsset(currentAsset) && (
              <AudioPlayer
                asset={currentAsset}
                isPlaying={isVideoPlaying}
                loading={isLoading || playedSeconds < 0.1}
              ></AudioPlayer>
            )}
          </Box>
          <Box
            // width={"100%"}
            // height={"100%"}
            sx={
              isMobile
                ? {
                    width: "100%",
                    height: "90%",
                    position: "absolute",
                    display: !isImageAsset(currentAsset) ? "initial" : "none",
                    zIndex: 15,
                    top: 0,
                    left: 0,
                  }
                : {
                    width: "100%",
                    height: "90%",
                    position: "absolute",
                    display: !isImageAsset(currentAsset) ? "initial" : "none",
                    zIndex: 15,
                    top: 0,
                    left: 0,
                  }
            }
            onClick={() => {
              if (isDebug) {
                myToast.success("On Click on the player");
              }
              setPlaying(false);
            }}
          >
            <VideoPlayer
              key={currentAsset?.id ?? "no-asset"}
              playerRef={playerRef}
              url={mediaUrl}
              playing={isVideoPlaying}
              muted={volume === 0 || isMuted}
              volume={(volume || 80) / 100}
              onProgress={(played, playedSeconds) => {
                setPlayedSeconds(playedSeconds);
              }}
              onReady={() => {
                setPlaying(true);
                setLoading(false);
                setPlaybackError(null);
                if (isDebug) {
                  myToast.success(`${assetType} is ready`);
                }
              }}
              onDuration={(e) => {
                setDuration(Math.floor(e));
              }}
              onBufferEnd={() => {
                setPlaying(true);
                setLoading(false);
              }}
              onStart={() => {
                setReady(true);
                
                // Mark video as started and clear timeout
                if (isVideoAsset(currentAsset) && currentAsset?.id) {
                  videoStartedRef.current = true;
                  
                  // Clear the 3-second timeout since video started
                  if (videoStartTimeoutRef.current) {
                    clearTimeout(videoStartTimeoutRef.current);
                    videoStartTimeoutRef.current = null;
                  }
                  
                  // Send watched analytics when video starts (only once per asset)
                  if (watchedAnalyticsSentRef.current !== currentAsset.id) {
                    watchedAnalyticsSentRef.current = currentAsset.id;
                    apiAssetAnalyticsWatchedAsset(currentAsset.id).catch((err) => {
                      console.error("Failed to send watched analytics:", err);
                    });
                  }
                }
                
                if (isDebug) {
                  myToast.success(`${assetType} starts playing`);
                }
              }}
              onPlay={() => {
                setReady(true);
                setPlaying(true);
                setLoading(false);
                setPlaybackError(null);
                
                // Mark video as started and clear timeout
                if (isVideoAsset(currentAsset) && currentAsset?.id) {
                  videoStartedRef.current = true;
                  
                  // Clear the 3-second timeout since video started playing
                  if (videoStartTimeoutRef.current) {
                    clearTimeout(videoStartTimeoutRef.current);
                    videoStartTimeoutRef.current = null;
                  }
                  
                  // Send watched analytics when video actually starts playing (only once per asset)
                  if (watchedAnalyticsSentRef.current !== currentAsset.id) {
                    watchedAnalyticsSentRef.current = currentAsset.id;
                    apiAssetAnalyticsWatchedAsset(currentAsset.id).catch((err) => {
                      console.error("Failed to send watched analytics:", err);
                    });
                  }
                }
              }}
              onPause={() => {
                if (isVideoAsset(currentAsset)) {
                  setPlaying(false);
                }
              }}
              onEnded={() => {
                if (isDebug) {
                  myToast.success(`${assetType} is ended`);
                }
                apiAssetAnalyticsFullPlayed(currentAsset?.id);
                onActionNext(true); // Pass true to indicate automatic navigation from video end
              }}
              onError={(error) => {
                console.warn(`Playback error for ${currentAsset?.id}:`, error?.name || error);

                if (error.name === "NotAllowedError") {
                  if (isVideoAsset(currentAsset)) {
                    setPlaying(false);
                  }
                  return;
                }

                // Try fallback URLs before skipping
                if (isVideoAsset(currentAsset) && currentAsset?.id) {
                  const playbackCandidates = getVideoPlaybackCandidates(currentAsset);
                  const triedUrls = triedPlaybackUrlsRef.current[currentAsset.id] || [];
                  const fallbackUrl = playbackCandidates.find((url) => !triedUrls.includes(url));

                  if (fallbackUrl) {
                    triedPlaybackUrlsRef.current[currentAsset.id] = [...triedUrls, fallbackUrl];
                    setMediaUrl(fallbackUrl);
                    setLoading(true);
                    setPlaying(true);
                    return;
                  }

                  // All candidates exhausted — silently skip to next
                  apiAssetAnalyticsErrorAsset(currentAsset.id, `Playback failed: ${error?.name || "unknown"}`).catch(() => {});
                }

                // Quietly skip to next asset
                if (playbackAssets?.length > 1 && currentAsset?.id !== skippedAssetRef.current) {
                  skippedAssetRef.current = currentAsset?.id;
                  setLoading(true);
                  setTimeout(() => {
                    onActionNext(true);
                  }, 100);
                } else {
                  setLoading(false);
                  setPlaying(false);
                }
              }}
              caption={currentAsset?.file?.outputCC}
            ></VideoPlayer>
            {playbackError && !isLoading ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 25,
                  background: "rgba(0,0,0,0.55)",
                  p: 2,
                }}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography variant="body2" color="common.white" sx={{ maxWidth: 720 }}>
                  {playbackError}
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Box
            alignItems="center"
            flexDirection="column"
            sx={{
              display: {
                xs: "flex",
                md: "flex",
              },
              position: "absolute",
              zIndex: 999,
              // right: isPlaying ? -60 : 10,
              right: 10,
              bottom: {
                xs: 60,
                sm: 80,
                md: 120,
              },
              // transition: " right  0.5s",
            }}
          >
            {domainInfo?.componentList?.includes("Reservation") ? (
              <IconButton
                sx={{ color: "#D3D3D3" }}
                onClick={() => {
                  onHandleComponentAction("Reservation");
                }}
              >
                <Ticket size={24} />
              </IconButton>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("PrivateComment") ? (
              <IconButton
                sx={{ color: "#D3D3D3" }}
                onClick={() => {
                  onHandleComponentAction("PrivateComment");
                }}
              >
                <AlertCircle size={24} />
              </IconButton>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("Info+") && currentAsset?.desc?.body ? (
              <IconButton
                sx={{ color: "#D3D3D3" }}
                onClick={() => {
                  onHandleComponentAction("Info+");
                }}
              >
                <FileText size={24} />
              </IconButton>
            ) : (
              <></>
            )}
            <IconButton
              sx={{ color: "#D3D3D3" }}
              onClick={() => {
                onHandleComponentAction("Comment");
              }}
            >
              <MessageSquare size={24} />
            </IconButton>
            {domainInfo?.componentList?.includes("Bookmark") ? (
              <>
                <IconButton
                  sx={{ color: "#D3D3D3" }}
                  onClick={() => {
                    onHandleComponentAction("Bookmark");
                  }}
                >
                  <Bookmark
                    fill={isBookmarked(currentAsset) ? "currentColor" : "none"}
                    size={24}
                    sx={{
                      fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                    }}
                  />
                </IconButton>
              </>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("Thumb up/down") ? (
              <>
                <IconButton
                  sx={{ color: "#D3D3D3" }}
                  onClick={() => {
                    onHandleComponentAction("Thumb up");
                  }}
                >
                  <ThumbsUp
                    fill={statusOfAssets[currentAsset?.id]?.liked ? "currentColor" : "none"}
                    size={24}
                    sx={{
                      fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                    }}
                  />
                </IconButton>
                <span style={{ color: "#D3D3D3" }}>{analytics?.liked ? analytics?.liked : 0}</span>
              </>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("Thumb up/down") ? (
              <>
                <IconButton
                  onClick={() => {
                    onHandleComponentAction("Thumb down");
                  }}
                >
                  <ThumbsDown
                    fill={statusOfAssets[currentAsset?.id]?.disliked ? "currentColor" : "none"}
                    size={24}
                    sx={{
                      fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                    }}
                  />
                </IconButton>
                <span style={{ color: "#D3D3D3" }}>{analytics?.disliked ? analytics?.disliked : 0}</span>
              </>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("Information") ? (
              <IconButton
                sx={{ color: "#D3D3D3" }}
                size="medium"
                onClick={() => {
                  onHandleComponentAction("Information");
                }}
                // ref={InfoButtonRef}
              >
                <Info size={24} />
              </IconButton>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("Screenshot") ? (
              <IconButton
                sx={{ color: "#D3D3D3" }}
                size="medium"
                onClick={() => {
                  onHandleComponentAction("Screenshot");
                }}
              >
                <Camera size={24} />
              </IconButton>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("PlayerWatch") ? (
              <Box sx={{ ml: 0.5, my: 0.5 }}>
                <PlayerWatch
                  openNoteEditor={openNoteEditor}
                  setOpenNoteEditor={setOpenNoteEditor}
                  playerStatus={playerStatus}
                  asset={currentAsset}
                  setVideoPlaying={setPlaying}
                  watchRef={WatchButtonRef}
                ></PlayerWatch>
              </Box>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("CreatorThumbnail") ? (
              <Box sx={{ mx: 0.5, my: 0.5 }}>
                <CreatorThumbnail
                  // buttonRef={CreatorButtonRef}
                  creator={assetOwner}
                  hideName={true}
                  onClick={() => {
                    onHandleComponentAction("CreatorThumbnail");
                  }}
                  type="assetPlayer"
                ></CreatorThumbnail>
              </Box>
            ) : (
              <></>
            )}
          </Box>
        </Box>
        {shouldShowCategoryBar ? (
          <CategoryBar
            asset={currentAsset}
            filter={filter}
            isImage={isImageAsset(currentAsset)}
            categories={nonEmptyCategories}
            currentCategoryId={currentCategoryId}
            onNext={(category) => {
              if (category?.id) jumpToCategory(category.id);
            }}
            onPrev={(category) => {
              if (category?.id) jumpToCategory(category.id);
            }}
          />
        ) : null}
        {(currentAsset?.desc?.title || currentAsset?.desc?.short || currentAsset?.name) && (
          <DescriptionBox
            currentAsset={currentAsset}
          />
        )}
        <CustomControls
          playerStatus={playerStatus}
          numberOfAssets={playbackAssets?.length ?? 0}
          onPrev={() => {
            onActionPrev();
          }}
          onNext={() => {
            onActionNext();
          }}
          onSeek={(value) => {
            playerRef?.current?.seekTo(value, "seconds");
            if (isDebug) {
              myToast.success("Seeked to custom seconds");
            }
          }}
          disableSeek={isImageAsset(currentAsset)}
          currentAsset={currentAsset}
          onWatchFullVideo={handleWatchFullVideo}
          isViewingParent={isViewingParent}
          canWatchFullVideo={Boolean(currentAsset?.parentId && isParentPublished)}
        ></CustomControls>
        <Box
          className="asset-player-actions"
          display={"flex"}
          alignItems={"flex-end"}
          justifyContent="space-between"
          sx={{
            position: "absolute",
            right: "0.5rem",
            left: "0.5rem",
            zIndex: 30,
            bottom: 42,
          }}
        >
          {/* <IconButton
          onClick={() => {
            setMuted(!isMuted);
          }}
        >
          {isMuted ? (
            <VolumeX
              size={32}
              sx={{
                fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" },
              }}
            />
          ) : (
            <Volume2
              size={32}
              sx={{
                fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" },
              }}
            />
          )}
        </IconButton> */}

          {/* {isMobile ? (
          <Box className="flex items-center">
            {domainInfo?.componentList?.includes("PrivateComment") ? (
              <IconButton
                size="small"
                sx={{ color: "#fff" }}
                onClick={() => {
                  setOpendirectCommentDrawer(true);
                }}
              >
                <AlertCircle />
              </IconButton>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("Info+") ? (
              <IconButton
                size="small"
                sx={{ color: "#fff" }}
                onClick={() => {
                  setOpenDescriptionDrawer(true);
                }}
              >
                <FileText />
              </IconButton>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("Comment") ? (
              <IconButton
                size="small"
                sx={{ color: "#fff" }}
                onClick={() => {
                  setOpenCommentDrawer(true);
                }}
              >
                <MessageSquare />
              </IconButton>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("Bookmark") ? (
              <>
                <IconButton
                  sx={{ color: "#ffffff" }}
                  size="small"
                  onClick={() => {
                    if (isBookmarked(currentAsset)) {
                      removeAssetBookmark(currentAsset).then(() => {
                        setOpenBookmarkMenu(false);
                      });
                    } else {
                      setOpenBookmarkMenu(true);
                    }
                  }}
                  ref={bookmarkButtonRef}
                >
                  <Bookmark
                    fill={isBookmarked(currentAsset) ? "currentColor" : "none"}
                    size={32}
                    sx={{
                      fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                    }}
                  />
                </IconButton>
              </>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("Thumb up/down") ? (
              <IconButton
                size="small"
                sx={{ color: "#fff" }}
                onClick={() => {
                  if (!statusOfAssets[currentAsset?.id]?.liked) {
                    apiAssetAnalyticsLikeAsset(currentAsset?.id).then(() => {
                      actionLikeAnAsset(currentAsset?.id);
                    });
                  } else {
                    actionLikeAnAsset(currentAsset?.id, false);
                  }
                }}
              >
                <ThumbsUp
                  fill={statusOfAssets[currentAsset?.id]?.liked ? "currentColor" : "none"}
                  size={32}
                  sx={{
                    fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                  }}
                />
              </IconButton>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("Thumb up/down") ? (
              <IconButton
                size="small"
                onClick={() => {
                  if (!statusOfAssets[currentAsset?.id]?.disliked) {
                    apiAssetAnalyticsDislikeAsset(currentAsset?.id).then(() => {
                      actionDislikeAnAsset(currentAsset?.id);
                    });
                  } else {
                    actionDislikeAnAsset(currentAsset?.id, false);
                  }
                }}
              >
                <ThumbsDown
                  fill={statusOfAssets[currentAsset?.id]?.disliked ? "currentColor" : "none"}
                  size={32}
                  sx={{
                    fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                  }}
                />
              </IconButton>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("Information") ? (
              <IconButton
                sx={{ color: "#fff" }}
                size="medium"
                onClick={() => {
                  setPlaying(false);
                  setOpenAssetDrawer(true);
                }}
                ref={InfoButtonRef}
              >
                <Info size={16} />
              </IconButton>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("Screenshot") ? (
              <IconButton
                sx={{ color: "#fff" }}
                size="medium"
                onClick={() => {
                  setPlaying(false);
                  setOpenScreenshotSelector(true);
                }}
              >
                <Camera size={16} />
              </IconButton>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("PlayerWatch") ? (
              <PlayerWatch
                playerStatus={playerStatus}
                asset={currentAsset}
                setVideoPlaying={setPlaying}
                watchRef={WatchButtonRef}
              ></PlayerWatch>
            ) : (
              <></>
            )}
            {domainInfo?.componentList?.includes("CreatorThumbnail") ? (
              <Box sx={{ mx: 0.5 }}>
                <CreatorThumbnail
                  buttonRef={CreatorButtonRef}
                  creator={assetOwner}
                  hideName={true}
                  onClick={() => {
                    setOpenCreatorDrawer(true);
                  }}
                  type="assetPlayer"
                ></CreatorThumbnail>
              </Box>
            ) : (
              <></>
            )}
          </Box>
        ) : (
          <></>
        )} */}
        </Box>

        <Box
          sx={{
            position: "absolute",
            height: "100%",
            width: "100%",
            top: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 1,
          }}
        >
          <ComponentDrawer
            currentAsset={currentAsset}
            playerStatus={playerStatus}
            setPlaying={setPlaying}
            assetOwner={assetOwner}
            isBookmarked={isBookmarked(currentAsset)}
            onClick={onHandleComponentAction}
            openNoteEditor={openNoteEditor}
            setOpenNoteEditor={setOpenNoteEditor}
          />
        </Box>
      </SwipeActionCatcher>
      <BookmarkMenu
        open={openBookmarkMenu}
        onClose={() => {
          setPlaying(true);
          setOpenBookmarkMenu(false);
        }}
        anchorEl={openBookmarkMenu ? bookmarkButtonRef?.current : null}
        onBookmark={(userFolder) => {
          addAssetBookmark(userFolder, currentAsset);
        }}
      ></BookmarkMenu>
      <AssetDrawer
        anchorEl={InfoButtonRef?.current}
        open={openAssetDrawer}
        onClose={() => {
          setPlaying(true);
          setOpenAssetDrawer(false);
        }}
        playerStatus={playerStatus}
        asset={currentAsset}
      ></AssetDrawer>
      <CreatorDrawer
        anchorEl={CreatorButtonRef?.current}
        open={openCreatorDrawer}
        onClose={() => {
          setPlaying(true);
          setOpenCreatorDrawer(false);
        }}
        creator={assetOwner}
      ></CreatorDrawer>
      <NoteDrawer
        open={noteState?.showed}
        onClose={() => {
          setPlaying(true);
          if (!noteState?.pinned) setNoteState({ ...noteState, showed: false });
        }}
        onPin={() => {
          setNoteState((noteState) => ({
            ...noteState,
            pinned: !noteState?.pinned,
          }));
        }}
        noteState={noteState}
      ></NoteDrawer>
      <DialogCapturedNote
        file={snapshotFile}
        asset={currentAsset}
        playerStatus={playerStatus}
        open={Boolean(snapshotFile)}
        onClose={() => {
          setPlaying(true);
          setSnapshotFile(null);
        }}
        onSaveEnded={() => {
          setSnapshotFile(null);
          setOpenScreenshotSelector(false);
          setPlaying(true);
        }}
      ></DialogCapturedNote>
      <CommentDrawer
        open={openCommentDrawer}
        setOpen={setOpenCommentDrawer}
        onClose={() => {
          setPlaying(true);
          setOpenCommentDrawer(false);
        }}
        asset={currentAsset}
      />
      <CommentDrawer
        open={openDirectCommentDrawer}
        setOpen={setOpendirectCommentDrawer}
        onClose={() => {
          setPlaying(true);
          setOpendirectCommentDrawer(false);
        }}
        asset={currentAsset}
        isDirect={true}
      />
      <ReservationDrawer
        open={openReservationDrawer}
        setOpen={setOpenReservationDrawer}
        onClose={() => {
          setPlaying(true);
          setOpenReservationDrawer(false);
        }}
        asset={currentAsset}
      />
      <DescriptionDrawer
        open={openDescriptionDrawer}
        body={currentAsset?.desc?.body}
        onClose={() => {
          setPlaying(true);
          setOpenDescriptionDrawer(false);
        }}
      />
    </>
  );
}

export default ForYouAssetPlayer;
